import axios from "axios";

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// "View as" override — facilitators/admins can browse the opposite program.
// AuthContext writes the override here (sessionStorage so it doesn't survive
// a closed browser, but does survive page reloads inside one session).
//
// SECURITY NOTE: This sessionStorage holds ONLY a non-sensitive program
// preference ("men" or "women"). Authentication tokens are NEVER stored
// client-side — they live exclusively in HttpOnly cookies set by the
// backend (`withCredentials: true` above forwards them automatically).
const VIEW_KEY = "tcpi-view-program";
export function setViewProgram(p) {
  try {
    if (p === "men" || p === "women") window.sessionStorage.setItem(VIEW_KEY, p);
    else window.sessionStorage.removeItem(VIEW_KEY);
  } catch (err) {
    // Private-mode browsers throw on sessionStorage writes; treat as no-op.
    console.warn("view-program persistence unavailable:", err?.message);
  }
}
export function getViewProgram() {
  try {
    return window.sessionStorage.getItem(VIEW_KEY);
  } catch (err) {
    console.warn("view-program read unavailable:", err?.message);
    return null;
  }
}

api.interceptors.request.use((config) => {
  const v = getViewProgram();
  if (!v) return config;
  // Only attach to endpoints that are program-scoped on the backend
  const url = (config.url || "");
  const isScoped =
    url.startsWith("/tracks") ||
    url.startsWith("/friday/");
  if (isScoped) {
    config.params = { ...(config.params || {}), program: v };
  }
  return config;
});

// 401 response interceptor.
// Background: previously, if a participant's auth cookie expired while they
// were already on a protected page (e.g. /tracks), every API call to that
// page would silently 401, the page would render its "data: []" fallback,
// and the user would just see a blank section with no error message. That
// was the root cause of the "women can't get to the tracks" report.
//
// We now intercept 401s globally and bounce the user to /login with a
// `?next=` so they can return to where they were. Exceptions: the login
// and auth endpoints themselves — those callers handle their own 401s.
const PUBLIC_AUTH_PATHS = ["/auth/login", "/auth/register", "/auth/me"];
let _redirecting = false;
api.interceptors.response.use(
  (resp) => resp,
  (err) => {
    const status = err?.response?.status;
    const url = err?.config?.url || "";
    if (status === 401 && !PUBLIC_AUTH_PATHS.some((p) => url.includes(p)) && !_redirecting) {
      _redirecting = true;
      // Use setTimeout so the rejected promise still surfaces to callers that
      // want to handle 401 themselves (e.g. show a toast first).
      setTimeout(() => {
        const here = window.location.pathname + window.location.search;
        const next = encodeURIComponent(here);
        window.location.assign(`/login?next=${next}&reason=session_expired`);
      }, 50);
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") return detail.message;
  if (detail && typeof detail.detail === "string") return detail.detail;
  try { return JSON.stringify(detail); } catch { return String(detail); }
}

// Build a user-readable message from any axios error.
// Preference order: backend `detail` -> HTTP status hint -> network hint.
// HTTP status constants used in describeAxiosError below.
const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;
const HTTP_TOO_MANY_REQUESTS = 429;
const HTTP_INTERNAL_SERVER_ERROR = 500;
const HTTP_BAD_GATEWAY = 502;
const HTTP_SERVICE_UNAVAILABLE = 503;
const HTTP_GATEWAY_TIMEOUT = 504;

export function describeAxiosError(err) {
  if (!err) return "Something went wrong. Please try again.";
  const resp = err.response;
  // Server replied with an HTTP status
  if (resp) {
    const fromDetail = formatApiError(resp.data?.detail);
    if (fromDetail) return fromDetail;
    const fromMessage = formatApiError(resp.data?.message);
    if (fromMessage) return fromMessage;
    const status = resp.status;
    if (status === HTTP_UNAUTHORIZED) return "Invalid email or password.";
    if (status === HTTP_FORBIDDEN) return "Access denied. Your account may be deactivated.";
    if (status === HTTP_NOT_FOUND) return "We couldn't find that resource (404).";
    if (status === HTTP_TOO_MANY_REQUESTS) return "Too many requests. Please wait a moment and retry.";
    if (
      status === HTTP_BAD_GATEWAY ||
      status === HTTP_SERVICE_UNAVAILABLE ||
      status === HTTP_GATEWAY_TIMEOUT
    )
      return `Server is unavailable right now (${status}). Please try again in a moment.`;
    if (status >= HTTP_INTERNAL_SERVER_ERROR) return `Server error (${status}). Please try again.`;
    return `Request failed (${status}).`;
  }
  // No response — network/CORS/offline
  if (err.code === "ERR_NETWORK" || /Network Error/i.test(err.message || ""))
    return "Network error — can't reach the server. Check your internet, then retry.";
  if (err.code === "ECONNABORTED" || /timeout/i.test(err.message || ""))
    return "Request timed out. Please try again.";
  return err.message || "Something went wrong. Please try again.";
}
